/**
 * Face Grouping Service
 *
 * Provides API access for the Face Grouping Tester admin tool
 *
 * Data Source: UserEventsAggregation table (match events with face detection metadata)
 */

import { API } from './apiClient';
import { API_USER_EVENTS_AGGREGATION, API_TENANT_CONFIG } from '../../constants';
import { getCapturedImageDisplayUrl } from '../s3/captureUrlService';

/**
 * Fetch recent processed images with face detection data from UserEventsAggregation
 *
 * Queries detection events (eventType='detection') that contain complete face detection data:
 * - capturedImageUrl: S3 URL of the captured image
 * - allFaceDetails: Array of ALL detected faces with bounding boxes and confidence
 * - detectedFaceCount: Total faces detected
 * - clusterMetadata: Clustering information from V3.2 algorithm
 *
 * Detection events are created optimistically when faces are detected, before matching.
 * This provides complete face data for ALL captures, not just successful matches.
 *
 * @param {Object} params
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.limit - Number of images per page (default: 20)
 * @returns {Promise<Object>} { images: [...], totalPages: number, nextToken: string }
 */
export async function fetchRecentImages({ page = 0, limit = 20 } = {}) {
  try {
    // Query UserEventsAggregation for recent detection events
    const response = await API.get(API_USER_EVENTS_AGGREGATION, '/events', {
      queryStringParameters: {
        eventType: 'detection', // Detection events have complete face data
        limit: limit.toString()
        // TODO: Support pagination with nextToken
      }
    });

    // Transform detection events into image data for grouping tester
    const images = transformDetectionEventsToImages(response.events || []);

    // Estimate total pages (rough estimate since we don't have total count)
    const totalPages = response.nextToken ? page + 2 : page + 1;

    return {
      images,
      totalPages,
      currentPage: page,
      totalImages: images.length,
      nextToken: response.nextToken
    };
  } catch (error) {
    console.error('[FaceGroupingService] Error fetching images:', error);
    throw new Error(`Failed to fetch images: ${error.message}`);
  }
}

/**
 * Transform detection events into image data for Face Grouping Tester
 *
 * Detection events contain ALL detected faces with complete bounding box data,
 * making them perfect for testing clustering/grouping algorithms.
 *
 * @param {Array} events - Detection events from UserEventsAggregation
 * @returns {Array} Images with complete face detection data
 */
function transformDetectionEventsToImages(events) {
  return events.map(event => {
    const metadata = event.metadata || {};
    const s3Key = metadata.capturedImageUrl || '';

    // Convert S3 key to public URL for display
    const displayUrl = getCapturedImageDisplayUrl(s3Key);

    // Normalize face details to uppercase format (BoundingBox, Confidence)
    // Detection events may have lowercase properties from Rekognition
    const normalizedFaceDetails = (metadata.allFaceDetails || []).map(face => ({
      BoundingBox: face.BoundingBox || face.boundingBox || {},
      Confidence: face.Confidence !== undefined ? face.Confidence : face.confidence,
      Landmarks: face.Landmarks || face.landmarks
    }));

    return {
      imageId: event.eventId,
      s3Key: s3Key,
      url: displayUrl,
      faceDetails: normalizedFaceDetails,
      timestamp: event.timestamp,
      userId: event.userId,
      detectedFaceCount: metadata.detectedFaceCount || 0,
      clusterMetadata: metadata.clusterMetadata || null
    };
  }).filter(image => image.url && image.faceDetails.length > 0); // Only include images with valid URLs and detected faces
}

/**
 * Extract S3 key from full S3 URL or return S3 key if already in key format
 * @param {string} urlOrKey - Full S3 URL or S3 key
 * @returns {string} S3 key
 */
function extractS3KeyFromUrl(urlOrKey) {
  if (!urlOrKey) return '';

  // If it's already an S3 key (starts with "public/" or doesn't start with http), return as-is
  if (!urlOrKey.startsWith('http')) {
    return urlOrKey;
  }

  // Otherwise parse as URL
  try {
    const urlObj = new URL(urlOrKey);
    // Remove leading slash and decode
    return decodeURIComponent(urlObj.pathname.substring(1));
  } catch (error) {
    console.warn('[FaceGroupingService] Could not parse S3 URL:', urlOrKey);
    return urlOrKey;
  }
}

/**
 * Load saved clustering settings from TenantConfig
 *
 * @returns {Promise<Object>} Clustering configuration
 */
export async function loadClusteringSettings() {
  try {
    const { getClusteringConfig } = await import('./tenantConfigService');
    const config = await getClusteringConfig();
    return config;
  } catch (error) {
    console.error('[FaceGroupingService] Error loading settings:', error);
    // Return defaults if API call fails
    return {
      size_similarity_threshold: 0.5,
      proximity_distance_threshold: 0.4,
      min_face_confidence: 90
    };
  }
}

/**
 * Save clustering settings to TenantConfig
 *
 * @param {Object} settings - Clustering configuration to save
 * @returns {Promise<Object>} Updated configuration
 */
export async function saveClusteringSettings(settings) {
  try {
    const { updateClusteringConfig } = await import('./tenantConfigService');
    const config = await updateClusteringConfig(settings);
    return config;
  } catch (error) {
    console.error('[FaceGroupingService] Error saving settings:', error);
    throw new Error(`Failed to save settings: ${error.message}`);
  }
}
